import { useContext, useEffect, useRef } from 'react';
import {
    JsonHubProtocol,
    HubConnectionState,
    HubConnectionBuilder,
    LogLevel,
    HubConnection,
    IHttpConnectionOptions,
    HttpRequest,
    HttpClient,
} from '@microsoft/signalr';
import { useAuthStore } from '../context/AuthContext';
import { useConnectionStore } from '../context/ConnectionContext';
import { HttpResponse } from '@microsoft/signalr/dist/esm/HttpClient';
import { useAolUpdateHandler } from './useAolUpdateHandler';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import useEvent from './useEvent';
import { useToast } from './useToast';
import { useVesselStore } from '../context/VesselContext';
import { SignalRResponse } from '../types/Dto/SignalRResponse';
import { useSWService } from '../api/sw/useSWService';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from '../api/endpoints/queryParamsHelper';
import useApi from '../api/useApi';
import { useAolStore } from '../context/AolContext';
import useGetAolData from '../api/aol/useGetAolData';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import useCargoTemperatureApi from '../api/cargo-temp-log/useCargoTempLogApi';
import getContext from '../context/AppSettingsContext';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import useAppFeature from './useAppFeature';
import { useCargoTempLogStore } from '../context/CargoTempLogContext';
import { useBunkerUpdateHandler } from './useBunkerUpdateHandler';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowagePlanResponseHandler } from 'src/api/responseHandlers/useStowagePlanResponseHandler';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import useGetActiveMasterPlanVersionApi from 'src/api/stowage/useGetActiveMasterPlanVersionApi';
import { useAppStatus } from '../context/AppStatusContext';
import { logSignalRDiagnostic } from './signalRTelemetry';
import {
    canIssueGroupJoinRequest,
    GroupJoinRetryScheduler,
    isConfirmedGroupJoinResponse,
    type ConnectionGeneration,
} from './connectionRecoveryGuard';
import { useEventStore } from 'src/context/EventContext';
import { ConnectionRecoveryCoordinator } from './recovery/connectionRecoveryCoordinator';
import { StowageMasterPlanVersionRecovery } from './recovery/stowageRecovery';
import { useConnectionRecoveryOrchestrator } from './recovery/useConnectionRecoveryOrchestrator';
import { RECONNECT_NOTICE_TOAST_ID } from './recovery/aolBunkerRecovery';
import { useSignalRMessageHandlers } from './signalRMessageHandlers';

interface QueuedSignalRMessage {
    response: SignalRResponse;
    generation: ConnectionGeneration;
    vesselImoNumber?: number;
}

export interface EventApplicationScope {
    generation: ConnectionGeneration;
    vesselImoNumber: number;
}

const useSignalRConnection = () => {
    const { setSignalRConnectionId, loggedInUser } = useAuthStore();
    const { selectedVessel } = useVesselStore();
    const { refetch: refetchAppStatus, apiUnavailable } = useAppStatus();
    const {
        setConnectionStatus,
        hubConnection,
        setHubConnection,
        connectedVesselGroup,
        setConnectedVesselGroup,
        setSyncState,
        setLastSignalRActivityAt,
        setRecoveryDomainState,
        resetSignalRSessionState,
        setSignalRRecoveryAction,
        isOfflineSimulated,
        setReconnectNotice,
        clearReconnectNotice,
        offlineLevel,
    } = useConnectionStore();
    const messageQueueRef = useRef<QueuedSignalRMessage[]>([]);
    const isProcessingRef = useRef(false);
    const messageRetryTimersRef = useRef(new Set<ReturnType<typeof setTimeout>>());
    const reconciliationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const processMessageQueueRef = useRef<() => Promise<void>>();
    // Mirrors the latest selectedVessel so long-lived callbacks created once (e.g. connection.onreconnected)
    // never act on a stale closure value.
    const selectedVesselRef = useRef(selectedVessel);
    selectedVesselRef.current = selectedVessel;
    const loggedInUserRef = useRef(loggedInUser);
    loggedInUserRef.current = loggedInUser;
    const hubConnectionRef = useRef(hubConnection);
    hubConnectionRef.current = hubConnection;
    const recoveryActionRef = useRef<() => Promise<void>>();
    const reconnectRecoveryCoordinatorRef = useRef(new ConnectionRecoveryCoordinator());
    const stowageMasterPlanVersionRecoveryRef = useRef(new StowageMasterPlanVersionRecovery());
    const recoveryRequiredRef = useRef(false);
    const wasOfflineOrDegradedRef = useRef(false);
    const hasConnectedOnceRef = useRef(false);
    const eventApplicationTailRef = useRef<Promise<void>>(Promise.resolve());
    const connectedVesselGroupRef = useRef(connectedVesselGroup);
    connectedVesselGroupRef.current = connectedVesselGroup;
    const connectionGenerationRef = useRef(0);
    const vesselGenerationRef = useRef(0);
    const sessionGenerationRef = useRef(0);
    const joinRetrySchedulerRef = useRef(new GroupJoinRetryScheduler());
    const { updateAolList, addNoonReport, updateOnDelete, updateAolStatusToOtherUsers, toggleToast, updateOnHoursSync, updateOnImosPortCallIdSync, reconcileGroupStatuses } =
        useAolUpdateHandler();
    const { handleUpdatedBunkering, handleUpdatedBunkeringStatus, handleDeletedBunkeringStatus, handleUpdatedRobs, toggleBunkerToast } =
        useBunkerUpdateHandler();
    const { toastSuccess, toastError, toastInfo, dismissToast } = useToast();
    const { updateDeltaEventArray } = useEvent();
    const {
        eventDataForVoyageList,
        setEventDataForVoyageList,
        clearCommittedEventRevisions,
        eventConsumerCount,
        eventReconciliationVersion,
        isEventReconciliationNeeded,
        getEventReconciliationGeneration,
        clearEventReconciliationNeeded,
    } = useEventStore();
    const eventDataForVoyageListRef = useRef(eventDataForVoyageList);
    eventDataForVoyageListRef.current = eventDataForVoyageList;
    const { refreshRequestsContaining, removeRequestsContaining } = useSWService();
    const { apiEndpoints } = useApiEndpoints();
    const { getNoonReportSection, getAolItems } = useGetAolData();
    const { getMasterStowagePlanFromApi } = useGetMasterPlanApi();
    const { getActiveMasterPlanVersion } = useGetActiveMasterPlanVersionApi();
    const { setAolItems, aolFilter, setHasSyncError } = useAolStore();
    const { cargoTempLogDates } = useCargoTempLogStore();
    const { getApiData, postApiData } = useApi();
    const location = useLocation();
    const locationRef = useRef(location.pathname);
    locationRef.current = location.pathname;
    const { getCargoTemperatureConfigs, getCargoTemperatureInputFields } = useCargoTemperatureApi();
    const appSettings = useContext(getContext());
    const { updateAolMissingTasks } = useUpdateMissingTasksIcon();
    const { isFeatureEnabled } = useAppFeature();
    const { productList, portNameList, berthNameList, stowagePlanRotationPortCallList, stowagePlanRotationCargoList } = useRotationStore();
    const { activeStowagePlan } = useStowagePlanStore();

    const MAX_SIGNALR_RETRY_ATTEMPTS = 15;
    const { onStowagePlanUpdated, onStowagePlanAdded, updateLoadedMasterStowagePlan } = useStowagePlanResponseHandler();

    const getCurrentGeneration = (): ConnectionGeneration => ({
        connection: connectionGenerationRef.current,
        vessel: vesselGenerationRef.current,
        session: sessionGenerationRef.current,
    });

    const isCurrentConnection = (connection: HubConnection, connectionGeneration: number): boolean =>
        !!loggedInUserRef.current && connectionGenerationRef.current === connectionGeneration && hubConnectionRef.current === connection;

    const clearJoinRetries = (): void => {
        joinRetrySchedulerRef.current.cancelAll();
    };

    const clearQueuedMessages = (): void => {
        messageQueueRef.current = [];
        isProcessingRef.current = false;
        messageRetryTimersRef.current.forEach((timer) => clearTimeout(timer));
        messageRetryTimersRef.current.clear();
    };

    const invalidateEventApplicationTail = (): void => {
        // An old transaction may still be awaiting its Event-consumer acknowledgement.
        // A new session must not be serialized behind that obsolete acknowledgement.
        eventApplicationTailRef.current = Promise.resolve();
    };

    const isCurrentEventApplicationScope = (scope: EventApplicationScope | undefined, dto?: TankersEventDataBM): scope is EventApplicationScope =>
        !!scope &&
        !!loggedInUserRef.current &&
        isCurrentGeneration(scope.generation) &&
        selectedVesselRef.current?.imoNumber === scope.vesselImoNumber &&
        (dto === undefined || dto.imoNumber === scope.vesselImoNumber);

    const serializeEventApplication = (scope: EventApplicationScope, apply: () => Promise<void>): Promise<void> => {
        const previous = eventApplicationTailRef.current.catch(() => undefined);
        const current = previous.then(async () => {
            if (!isCurrentEventApplicationScope(scope)) {
                return;
            }
            await apply();
        });
        eventApplicationTailRef.current = current;
        return current;
    };

    const isCurrentGeneration = (generation: ConnectionGeneration): boolean =>
        generation.connection === connectionGenerationRef.current &&
        generation.vessel === vesselGenerationRef.current &&
        generation.session === sessionGenerationRef.current;

    const invalidateSignalRSession = (): void => {
        hasConnectedOnceRef.current = false;
        wasOfflineOrDegradedRef.current = false;
        recoveryRequiredRef.current = false;
        clearJoinRetries();
        clearQueuedMessages();
        invalidateEventApplicationTail();
        reconnectRecoveryCoordinatorRef.current.invalidate();
        clearCommittedEventRevisions();
        clearReconnectNotice();
        dismissToast(RECONNECT_NOTICE_TOAST_ID);
        setSignalRConnectionId('');
        resetSignalRSessionState();
    };

    // Bounded requeue for messages that arrive while selectedVessel hasn't settled yet (e.g. right
    // after a vessel switch or during initial load). Without this, a message that momentarily fails
    // the imoNumber filter was silently and permanently discarded, even though the vessel selection
    // might catch up a moment later — a real (if narrow) window for losing an update.
    const MAX_VESSEL_MISMATCH_RETRIES = 5;
    const VESSEL_MISMATCH_RETRY_DELAY_MS = 200;

    const processMessageQueue = async () => {
        if (isProcessingRef.current) return;
        isProcessingRef.current = true;

        try {
            while (messageQueueRef.current.length > 0) {
                const queuedMessage = messageQueueRef.current.shift()!;
                const { response: message, generation, vesselImoNumber } = queuedMessage;

                if (!loggedInUserRef.current || !isCurrentGeneration(generation)) {
                    continue;
                }

                // Global broadcast messages — no IMO filter
                if (message.type === 'appStatusChanged') {
                    setLastSignalRActivityAt(new Date());
                    refetchAppStatus();
                    continue;
                }

                const connection = hubConnectionRef.current;
                if (
                    connection &&
                    isCurrentConnection(connection, generation.connection) &&
                    message.imoNumber === selectedVesselRef.current?.imoNumber?.toString()
                ) {
                    try {
                        setLastSignalRActivityAt(new Date());
                        const scope =
                            vesselImoNumber === undefined
                                ? undefined
                                : {
                                      generation,
                                      vesselImoNumber,
                                  };
                        await handleSignalRResponse(message.type, message, connection, scope);
                    } catch {
                        logSignalRDiagnostic('handler_failure', {
                            vesselImoNumber: selectedVesselRef.current?.imoNumber,
                            messageType: message.type,
                        });
                    }
                    continue;
                }

                const attempts = ((message as SignalRResponse & { _mismatchAttempts?: number })._mismatchAttempts ?? 0) + 1;
                if (attempts <= MAX_VESSEL_MISMATCH_RETRIES) {
                    (message as SignalRResponse & { _mismatchAttempts?: number })._mismatchAttempts = attempts;
                    const retryTimer = setTimeout(() => {
                        messageRetryTimersRef.current.delete(retryTimer);
                        if (!loggedInUserRef.current || !isCurrentGeneration(generation)) {
                            return;
                        }
                        messageQueueRef.current.push({ response: message, generation });
                        processMessageQueueRef.current?.();
                    }, VESSEL_MISMATCH_RETRY_DELAY_MS);
                    messageRetryTimersRef.current.add(retryTimer);
                }
            }
        } finally {
            isProcessingRef.current = false;
        }
    };

    // Keep ref in sync so the event listener always calls the latest version
    processMessageQueueRef.current = processMessageQueue;

    // Cleanup reconciliation timer on unmount
    useEffect(() => {
        return () => {
            // Deliberately reading .current inside the cleanup (once, into a local): this must
            // observe whatever the timer ref holds at the moment of unmount, not a value captured
            // when this effect last ran.
            // eslint-disable-next-line react-hooks/exhaustive-deps
            const timer = reconciliationTimerRef.current;
            if (timer) {
                clearTimeout(timer);
            }
            clearQueuedMessages();
        };
    }, []);

    const setupSignalRConnection = (url: string, imoNumber: number) => {
        clearJoinRetries();
        invalidateEventApplicationTail();
        reconnectRecoveryCoordinatorRef.current.invalidate();
        const connectionGeneration = ++connectionGenerationRef.current;
        const customHttpClient: HttpClient = new (class extends HttpClient {
            async send(request: HttpRequest): Promise<HttpResponse> {
                try {
                    let response;
                    if (request.method === 'POST') {
                        response = await postApiData({
                            endpoint: request.url,
                            disableAppSettingsApiEndpoint: true,
                            returnFullResponseObject: true,
                            data: request.content,
                        });
                    } else if (request.method === 'GET') {
                        response = await axios.get(request.url);
                    } else if (request.method === 'DELETE') {
                        response = await axios.delete(request.url);
                    } else {
                        response = await axios({ method: request.method?.toLowerCase(), url: request.url, data: request.content });
                    }

                    if (!response) {
                        return new HttpResponse(500, 'Internal Error', 'Request failed');
                    }

                    const responseBody = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                    if (response.status === 200 && response.data?.accessToken) {
                        localStorage.setItem('signalRConnection', responseBody);
                    }
                    return new HttpResponse(response.status, response.statusText, responseBody);
                } catch (error) {
                    return new HttpResponse(500, 'Internal Error', error?.message || 'Request failed');
                }
            }
        })();

        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => {
                return '';
            },
            httpClient: customHttpClient,
        };

        const connection = new HubConnectionBuilder()
            .withUrl(url, options)
            .withAutomaticReconnect()
            .withHubProtocol(new JsonHubProtocol())
            .configureLogging(LogLevel.Information)
            .build();

        // Note: to keep the connection open the serverTimeout should be
        // larger than the KeepAlive value that is set on the server
        // keepAliveIntervalInMilliseconds default is 15000 and we are using default
        // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
        connection.serverTimeoutInMilliseconds = 60000;

        // re-establish the connection if connection dropped
        connection.onclose((error) => {
            if (!isCurrentConnection(connection, connectionGeneration)) {
                return;
            }
            clearJoinRetries();
            console.assert(connection.state === HubConnectionState.Disconnected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Disconnected);
            }
            setConnectedVesselGroup(null);
            setSyncState('pending');
            setLastSignalRActivityAt(null);
            setSignalRConnectionId('');
            clearReconnectNotice();
            dismissToast(RECONNECT_NOTICE_TOAST_ID);
            recoveryRequiredRef.current = true;
            if (error) {
                logSignalRDiagnostic('connection_closed', {
                    vesselImoNumber: selectedVesselRef.current?.imoNumber,
                    reason: 'network',
                });
            }
        });

        connection.onreconnecting((_error) => {
            if (!isCurrentConnection(connection, connectionGeneration)) {
                return;
            }
            console.assert(connection.state === HubConnectionState.Reconnecting);
            setConnectionStatus(HubConnectionState.Reconnecting);
            setConnectedVesselGroup(null);
            setSyncState('pending');
            setLastSignalRActivityAt(null);
            clearReconnectNotice();
            dismissToast(RECONNECT_NOTICE_TOAST_ID);
            recoveryRequiredRef.current = true;
        });

        connection.onreconnected((connectionId) => {
            if (!isCurrentConnection(connection, connectionGeneration)) {
                return;
            }
            console.assert(connection.state === HubConnectionState.Connected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Connected);
            }
            setConnectedVesselGroup(null);
            setSyncState('pending');
            setLastSignalRActivityAt(null);
            setSignalRConnectionId(connectionId ?? '');

            // The Azure SignalR group membership is tied to the previous connectionId, which is
            // now invalid. Rejoin unconditionally using the current vessel (via ref, since this
            // closure was created once and must not rely on a stale selectedVessel value) —
            // connectedVesselGroup state cannot be trusted here because it still reflects the old
            // (now-invalid) group membership rather than the new connection's actual state.
            const imoNumber = selectedVesselRef.current?.imoNumber;
            if (imoNumber) {
                void joinVesselGroup(connection, imoNumber, 0, getCurrentGeneration());
            } else {
                setSyncState('pending');
            }
        });

        void startSignalRConnection(connection, url, imoNumber, connectionGeneration);
        return connection;
    };

    const startSignalRConnection = async (
        connection: HubConnection,
        url: string,
        imoNumber: number,
        connectionGeneration: number,
        attempts = 0
    ): Promise<void> => {
        if (connectionGenerationRef.current !== connectionGeneration || selectedVesselRef.current?.imoNumber !== imoNumber || !loggedInUserRef.current) {
            return;
        }

        try {
            await throwErrorIfOfflineIsSimulated();
            if (
                connectionGenerationRef.current !== connectionGeneration ||
                selectedVesselRef.current?.imoNumber !== imoNumber ||
                !loggedInUserRef.current
            ) {
                return;
            }
            if (connection) {
                await connection.start();

                if (
                    connection.connectionId &&
                    connection.state === HubConnectionState.Connected &&
                    connectionGenerationRef.current === connectionGeneration &&
                    selectedVesselRef.current?.imoNumber === imoNumber &&
                    loggedInUserRef.current
                ) {
                    if (setConnectionStatus) {
                        setConnectionStatus(HubConnectionState.Connected);
                    }
                    setSignalRConnectionId(connection.connectionId);
                    hubConnectionRef.current = connection;
                    setHubConnection(connection);

                    // Register message listener immediately so global broadcasts
                    // (e.g., appStatusChanged) are received before vessel group join
                    connection.on('newMessage', (res) => {
                        const generation = getCurrentGeneration();
                        const vesselImoNumber = selectedVesselRef.current?.imoNumber;
                        if (!loggedInUserRef.current || !isCurrentConnection(connection, connectionGeneration)) {
                            return;
                        }
                        messageQueueRef.current.push({ response: res, generation, vesselImoNumber });
                        void processMessageQueueRef.current?.();
                    });
                }
            }
        } catch (_error) {
            if (connectionGenerationRef.current !== connectionGeneration || selectedVesselRef.current?.imoNumber !== imoNumber || !loggedInUserRef.current) {
                return;
            }
            if (await isOfflineSimulated()) {
                logSignalRDiagnostic('negotiate_failure', { vesselImoNumber: imoNumber, reason: 'offline_simulated' });
                return;
            }
            logSignalRDiagnostic('negotiate_failure', { vesselImoNumber: imoNumber, reason: 'network' });

            if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                toastError('Connection lost — will auto-reconnect when online');
                return;
            }

            const delay = attempts < 5 ? 5000 : 30000;
            joinRetrySchedulerRef.current.schedule(
                () => void startSignalRConnection(connection, url, imoNumber, connectionGeneration, attempts + 1),
                delay
            );
        }
    };

    /**
     * Joins the given vessel group on the current connection, retrying with backoff on failure.
     * Unlike tryConnectToGroup, this always attempts the join — it does not gate on
     * connectedVesselGroup — because it's also used to re-establish group membership after a
     * reconnect, when connectedVesselGroup state cannot be trusted to reflect the new
     * connection's actual server-side membership.
     */
    const joinVesselGroup = async (
        connection: HubConnection,
        imoNumber: number,
        attempts = 0,
        generation: ConnectionGeneration = getCurrentGeneration()
    ): Promise<void> => {
        if (
            !imoNumber ||
            connection.state !== HubConnectionState.Connected ||
            !canIssueGroupJoinRequest({
                requestedVesselImoNumber: imoNumber,
                selectedVesselImoNumber: selectedVesselRef.current?.imoNumber,
                hasLoggedInUser: !!loggedInUserRef.current,
                isCurrentConnection: isCurrentConnection(connection, generation.connection),
                requestedGeneration: generation,
                currentGeneration: getCurrentGeneration(),
            })
        ) {
            return;
        }
        setSyncState('pending');

        try {
            const joinResponse = await connectToGroup(connection, appSettings.api, imoNumber);
            if (!isConfirmedGroupJoinResponse(joinResponse)) {
                throw new Error('JoinGroup did not return a successful API result.');
            }

            // A completed request may belong to a former vessel, logged-out user, or superseded
            // connection. It must never make the header report the current vessel as joined.
            // canIssueGroupJoinRequest already ANDs canApplyGroupJoinResult internally.
            if (
                !canIssueGroupJoinRequest({
                    requestedVesselImoNumber: imoNumber,
                    selectedVesselImoNumber: selectedVesselRef.current?.imoNumber,
                    hasLoggedInUser: !!loggedInUserRef.current,
                    isCurrentConnection: isCurrentConnection(connection, generation.connection),
                    requestedGeneration: generation,
                    currentGeneration: getCurrentGeneration(),
                })
            ) {
                return;
            }

            setConnectedVesselGroup(imoNumber);
            hasConnectedOnceRef.current = true;
            if (recoveryRequiredRef.current) {
                recoveryRequiredRef.current = false;
                void runReconnectRecovery(generation);
            } else {
                // This is the normal initial join, not a reconnect. Establish a lightweight
                // active MasterPlanVersion baseline but do not run recovery or alter initial loading.
                setSyncState('blocked-contract');
                void primeStowageMasterPlanVersion(generation);
            }
        } catch {
            if (
                !canIssueGroupJoinRequest({
                    requestedVesselImoNumber: imoNumber,
                    selectedVesselImoNumber: selectedVesselRef.current?.imoNumber,
                    hasLoggedInUser: !!loggedInUserRef.current,
                    isCurrentConnection: isCurrentConnection(connection, generation.connection),
                    requestedGeneration: generation,
                    currentGeneration: getCurrentGeneration(),
                })
            ) {
                return;
            }
            setSyncState('failed');
            if (await isOfflineSimulated()) {
                logSignalRDiagnostic('group_join_failure', { vesselImoNumber: imoNumber, reason: 'offline_simulated' });
                return;
            }

            logSignalRDiagnostic('group_join_failure', { vesselImoNumber: imoNumber, reason: 'network' });

            if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                toastError('Connection lost — will auto-reconnect when online');
                return;
            }

            const delay = attempts < 5 ? 5000 : 30000;
            joinRetrySchedulerRef.current.schedule(() => void joinVesselGroup(connection, imoNumber, attempts + 1, generation), delay);
        }
    };

    const tryConnectToGroup = async (connection: HubConnection, imoNumber: number, attempts = 0) => {
        if (!imoNumber || !connection || connection.state !== HubConnectionState.Connected) return;
        if (connectedVesselGroupRef.current !== imoNumber) {
            await joinVesselGroup(connection, imoNumber, attempts, getCurrentGeneration());
        }
    };

    useEffect(() => {
        vesselGenerationRef.current += 1;
        hasConnectedOnceRef.current = false;
        wasOfflineOrDegradedRef.current = false;
        recoveryRequiredRef.current = false;
        clearJoinRetries();
        invalidateEventApplicationTail();
        reconnectRecoveryCoordinatorRef.current.invalidate();
        clearCommittedEventRevisions();
        clearReconnectNotice();
        dismissToast(RECONNECT_NOTICE_TOAST_ID);
        setConnectedVesselGroup(null);
        setLastSignalRActivityAt(null);
        setSyncState('pending');
    // A vessel identity is the scope for group membership and event-sync timestamps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedVessel?.imoNumber]);

    useEffect(() => {
        if (!loggedInUser) {
            hasConnectedOnceRef.current = false;
            wasOfflineOrDegradedRef.current = false;
            recoveryRequiredRef.current = false;
            sessionGenerationRef.current += 1;
            stowageMasterPlanVersionRecoveryRef.current.invalidateSession(sessionGenerationRef.current);
            invalidateSignalRSession();
            clearReconnectNotice();
            dismissToast(RECONNECT_NOTICE_TOAST_ID);
            void stopSignalRConnection();
        }
    // The session generation is deliberately changed only on logout. Logging in starts a
    // new connection through the normal vessel lifecycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loggedInUser]);

    useEffect(() => {
        if (selectedVessel && hubConnection && hubConnection.state === HubConnectionState.Connected) {
            setSyncState('pending');
            if (connectedVesselGroup && connectedVesselGroup !== selectedVessel.imoNumber) {
                setConnectedVesselGroup(null);
                setLastSignalRActivityAt(null);
                void leaveGroup(hubConnection, appSettings.api, connectedVesselGroup).finally(() =>
                    void tryConnectToGroup(hubConnection, selectedVessel.imoNumber)
                );
            } else {
                void tryConnectToGroup(hubConnection, selectedVessel.imoNumber);
            }
        } else if (!selectedVessel) {
            setConnectedVesselGroup(null);
            setSyncState('pending');
            setLastSignalRActivityAt(null);
        }
    // Join/leave helpers close over useApi's unstable functions. This effect deliberately keys
    // off connection and vessel changes; including those helpers would repeatedly rejoin a group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hubConnection, selectedVessel]);

    // Full reconnect recovery orchestration (events, Stowage MasterPlanVersion, AOL/Bunker cache
    // eviction + notice, the debounced connectivity-recovery trigger, and the dual
    // offlineLevel/window online-online detection paths) is extracted to
    // useConnectionRecoveryOrchestrator. See that file for the preserved race-closing contract.
    const { runReconnectRecovery, primeStowageMasterPlanVersion, applyEventChanges } = useConnectionRecoveryOrchestrator({
        selectedVesselRef,
        loggedInUserRef,
        hubConnectionRef,
        eventDataForVoyageListRef,
        locationRef,
        recoveryRequiredRef,
        hasConnectedOnceRef,
        wasOfflineOrDegradedRef,
        reconnectRecoveryCoordinatorRef,
        stowageMasterPlanVersionRecoveryRef,
        isCurrentGeneration,
        getCurrentGeneration,
        isCurrentEventApplicationScope,
        serializeEventApplication,
        updateDeltaEventArray,
        eventDataForVoyageList,
        eventConsumerCount,
        eventReconciliationVersion,
        isEventReconciliationNeeded,
        getEventReconciliationGeneration,
        clearEventReconciliationNeeded,
        setEventDataForVoyageList,
        setRecoveryDomainState,
        setSyncState,
        setReconnectNotice,
        clearReconnectNotice,
        getApiData,
        apiEndpoints,
        removeRequestsContaining,
        isFeatureEnabled,
        getActiveMasterPlanVersion,
        getMasterStowagePlanFromApi,
        toastInfo,
        dismissToast,
        offlineLevel,
        apiUnavailable,
        joinVesselGroup,
    });

    const { handleSignalRResponse } = useSignalRMessageHandlers({
        selectedVessel,
        loggedInUser,
        hubConnection,
        locationPathname: location.pathname,
        aolFilter,
        cargoTempLogDates,
        activeStowagePlan,
        productList,
        portNameList,
        berthNameList,
        stowagePlanRotationPortCallList,
        stowagePlanRotationCargoList,
        reconciliationTimerRef,
        isCurrentEventApplicationScope,
        serializeEventApplication,
        applyEventChanges,
        updateAolList,
        addNoonReport,
        updateOnDelete,
        updateAolStatusToOtherUsers,
        toggleToast,
        updateOnHoursSync,
        updateOnImosPortCallIdSync,
        reconcileGroupStatuses,
        handleUpdatedBunkering,
        handleUpdatedBunkeringStatus,
        handleDeletedBunkeringStatus,
        handleUpdatedRobs,
        toggleBunkerToast,
        toastSuccess,
        toastError,
        getApiData,
        apiEndpoints,
        refreshRequestsContaining,
        removeRequestsContaining,
        getNoonReportSection,
        getAolItems,
        getMasterStowagePlanFromApi,
        setAolItems,
        setHasSyncError,
        getCargoTemperatureConfigs,
        getCargoTemperatureInputFields,
        updateAolMissingTasks,
        isFeatureEnabled,
        onStowagePlanUpdated,
        onStowagePlanAdded,
        updateLoadedMasterStowagePlan,
    });

    const leaveGroup = async (connection: HubConnection, url: string, imoNumber: number) => {
        return await getApiData({
            endpoint: url + 'LeaveGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
    };

    const stopSignalRConnection = async (): Promise<void> => {
        invalidateSignalRSession();
        connectionGenerationRef.current += 1;
        const connection = hubConnectionRef.current;
        const joinedVesselGroup = connectedVesselGroupRef.current;
        try {
            if (connection) {
                // Explicitly leave the group (rather than relying solely on the server noticing the
                // connection close) and await the full stop before returning. Previously this fired
                // hubConnection.stop() without awaiting it, so a caller that immediately created and
                // joined a new connection (e.g. the forced tab-wake reconnect) could end up with BOTH
                // the old and new connections registered in the same group for a brief window —
                // causing a single server-side broadcast to be delivered twice to the same client.
                if (joinedVesselGroup) {
                    void leaveGroup(connection, appSettings.api, joinedVesselGroup).catch(() => undefined);
                }
                await connection.stop();
            }
        } catch {
            // Cleanup must not depend on the hub implementation reaching Disconnected. Keep the
            // diagnostic bounded: the stop exception may contain connection or transport data.
            logSignalRDiagnostic('connection_stop_failure', {
                vesselImoNumber: selectedVesselRef.current?.imoNumber,
                reason: 'network',
            });
        } finally {
            hubConnectionRef.current = null;
            setHubConnection(null);
            localStorage.setItem('signalRConnection', '');
        }
    };

    const connectToGroup = async (connection: HubConnection, url: string, imoNumber: number): Promise<unknown> => {
        const result = await getApiData({
            endpoint: url + 'JoinGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
        return result;
    };

    const requestSignalRRecovery = async (): Promise<void> => {
        const imoNumber = selectedVesselRef.current?.imoNumber;
        if (!imoNumber || !loggedInUserRef.current) {
            return;
        }

        clearReconnectNotice();
        dismissToast(RECONNECT_NOTICE_TOAST_ID);

        const connection = hubConnectionRef.current;
        if (connection?.state === HubConnectionState.Connected) {
            // An explicit retry is a request to rerun every guarded recovery
            // adapter, including one that failed on an earlier connected retry.
            recoveryRequiredRef.current = true;
            await joinVesselGroup(connection, imoNumber);
            return;
        }

        if (connection) {
            recoveryRequiredRef.current = true;
            await stopSignalRConnection();
        }
        recoveryRequiredRef.current = true;
        setupSignalRConnection(appSettings.api, imoNumber);
    };

    recoveryActionRef.current = requestSignalRRecovery;

    useEffect(() => {
        setSignalRRecoveryAction(async () => {
            await recoveryActionRef.current?.();
        });

        return () => {
            setSignalRRecoveryAction(null);
        };
    }, [setSignalRRecoveryAction]);

    useEffect(() => {
        return () => {
            clearJoinRetries();
            clearQueuedMessages();
            invalidateEventApplicationTail();
            connectionGenerationRef.current += 1;
        };
    // This hook owns the retry scheduler for its lifetime.
    }, []);

    const throwErrorIfOfflineIsSimulated = async () => {
        const _isOfflineSimulated = await isOfflineSimulated();
        if (_isOfflineSimulated) throw new Error('offline');
    };

    return { setupSignalRConnection, stopSignalRConnection };
};

export default useSignalRConnection;
